import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';

interface AccordionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  badge?: number;
}

const Accordion: React.FC<AccordionProps> = React.memo(({
  title,
  children,
  defaultExpanded = true,
  expanded,
  onToggle,
  badge
}) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Handle controlled mode (when expanded prop is provided)
  const isControlled = expanded !== undefined;
  const currentExpanded = isControlled ? expanded : isExpanded;

  // Update internal state when defaultExpanded prop changes (for uncontrolled mode)
  useEffect(() => {
    if (!isControlled) {
      setIsExpanded(defaultExpanded);
    }
  }, [defaultExpanded, isControlled]);

  // Memoize the toggle function to prevent unnecessary re-renders
  const toggleAccordion = useCallback(() => {
    const newExpanded = !currentExpanded;

    if (!isControlled) {
      // Uncontrolled mode - update internal state
      setIsExpanded(newExpanded);
    }

    // Call onToggle callback if provided
    onToggle?.(newExpanded);
  }, [currentExpanded, isControlled, onToggle]);

  // Memoize the content styles to prevent recreation
  const contentStyles = useMemo(() => ({
    maxHeight: currentExpanded ? '2000px' : '0',
    opacity: currentExpanded ? 1 : 0,
    overflow: currentExpanded ? 'auto' : 'hidden',
    transition: 'max-height 0.25s ease, opacity 0.25s ease', // Slightly faster for better responsiveness
  }), [currentExpanded]);

  // Memoize the chevron styles
  const chevronStyles = useMemo(() => ({
    color: theme.colors.textSecondary,
    fontSize: '14px',
    fontWeight: '600',
    transform: currentExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.25s ease', // Slightly faster for better responsiveness
  }), [currentExpanded, theme.colors.textSecondary]);

  return (
    <div style={{
      backgroundColor: theme.colors.card,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: '12px',
      marginBottom: '16px',
      overflow: 'hidden',
      fontFamily: theme.fonts.primary,
    }}>
      <button
        onClick={toggleAccordion}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 16px',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: theme.fonts.primary,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.surface;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
        }}>
          <span style={{
            color: theme.colors.text,
            fontSize: theme.typography.h3.fontSize,
            fontWeight: theme.typography.h3.fontWeight,
            fontFamily: theme.fonts.display,
            lineHeight: theme.typography.h3.lineHeight,
            letterSpacing: theme.typography.h3.letterSpacing,
            marginRight: '8px',
          }}>
            {title}
          </span>
          {badge && badge > 0 && (
            <div style={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.primaryText,
              borderRadius: '12px',
              padding: '2px 8px',
              minWidth: '20px',
              textAlign: 'center',
              fontSize: theme.typography.caption.fontSize,
              fontWeight: '600',
              fontFamily: theme.fonts.secondary,
              letterSpacing: theme.typography.caption.letterSpacing,
            }}>
              {badge}
            </div>
          )}
        </div>
        <div style={chevronStyles}>
          ▼
        </div>
      </button>

      <div style={contentStyles}>
        {children}
      </div>
    </div>
  );
});

export default Accordion; 